import * as React from 'react'
import { useMemo } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Grid from '@mui/material/Grid'
import { ENTITY_TYPES, LocationDTO } from '@sales-center/common'
import { EntityDetail } from '@user/store/map/typings'
import { useData } from '../hooks/useData'
import { NextActionDate } from '@user/components/EntityCards/atoms/NextActionDate/NextActionDate'
import { LocationLink } from '../../LocationLink'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import TodayIcon from '@mui/icons-material/Today'

type Props = {
  entityDetails: EntityDetail | null
  serviceLocation: LocationDTO
  variant: string
  defaultOpen?: boolean
  canUpdate?: boolean
}

function OverviewFieldBox({
  icon,
  label,
  children,
  pb,
}: {
  icon?: React.ReactNode
  label: string
  pb?: number
  children: React.ReactNode
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        pb: pb,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {icon}
        <Typography
          variant="caption"
          sx={{
            textTransform: 'uppercase',
          }}
        >
          {label}
        </Typography>
      </Box>
      <Box>{children}</Box>
    </Paper>
  )
}

const iconSx = { fontSize: 16, color: 'text.primary' }

export function OverviewFields(props: Props) {
  const { entityDetails, serviceLocation, variant, canUpdate } = props
  const isPendingLead = variant === 'pendinglead' || variant === 'draftlead'

  const { createdDateFormatted, totalValueFormatted, status } = useData({
    entityDetails,
    serviceLocation,
    variant,
    defaultOpen: props.defaultOpen,
  })

  const entityTypeForNextAction = useMemo(() => {
    return variant.toLowerCase() === 'lead'
      ? ENTITY_TYPES.Lead
      : ENTITY_TYPES.Opportunity
  }, [variant])

  return (
    <Box sx={{ mb: 1.5 }}>
      <Grid container spacing={'14px'}>
        {!isPendingLead && (
          <>
            <Grid item xs={6}>
              <OverviewFieldBox
                icon={<CalendarTodayIcon sx={iconSx} />}
                label="Next Action"
                pb={0}
              >
                <NextActionDate
                  id={entityDetails?.id ?? 0}
                  noTitle
                  nextActionDate={
                    entityDetails?.nextActionDate
                      ? new Date(entityDetails.nextActionDate)
                      : null
                  }
                  entity={entityTypeForNextAction}
                  disabled={!canUpdate}
                  updateEntityDetails
                  containerStyles={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  textOnly
                />
              </OverviewFieldBox>
            </Grid>
            <Grid item xs={6}>
              <OverviewFieldBox
                icon={<AccessTimeIcon sx={iconSx} />}
                label="Age"
              >
                <Typography variant="subtitle2">
                  {entityDetails?.age !== undefined &&
                  entityDetails?.age !== null
                    ? `${entityDetails.age} ${entityDetails.age === 1 ? 'day' : 'days'}`
                    : '-'}
                </Typography>
              </OverviewFieldBox>
            </Grid>
            <Grid item xs={6} mt={'-2px'}>
              <OverviewFieldBox
                icon={<AttachMoneyIcon sx={iconSx} />}
                label="Total Value"
              >
                <Typography variant="subtitle2">
                  {totalValueFormatted}
                </Typography>
              </OverviewFieldBox>
            </Grid>
          </>
        )}
        <Grid item xs={6} mt={!isPendingLead ? '-2px' : 0}>
          <OverviewFieldBox icon={<TodayIcon sx={iconSx} />} label="Created">
            <Typography variant="subtitle2">
              {createdDateFormatted || '-'}
            </Typography>
          </OverviewFieldBox>
        </Grid>
        <Grid item xs={6} mt={!isPendingLead ? '-2px' : 0}>
          <OverviewFieldBox label="Location">
            {serviceLocation?.locationCode &&
            serviceLocation?.externalLinkId ? (
              <LocationLink
                externalLocationId={serviceLocation.externalLinkId}
                externalLocationCode={serviceLocation.locationCode}
                billingLocation={false}
                typographyVariant="subtitle2"
                fontSize="14px"
                noLabel
                noIcon={false}
                containerSx={{ alignContent: 'center' }}
                linkButtonSx={{
                  minWidth: 'fit-content',
                  maxWidth: 'fit-content',
                  padding: 0,
                  fontSize: '14px',
                }}
              />
            ) : (
              <Typography variant="body2">-</Typography>
            )}
          </OverviewFieldBox>
        </Grid>
        {!isPendingLead && (
          <Grid item xs={6} mt={'-2px'}>
            <OverviewFieldBox label="Status">
              <Typography variant="subtitle2">{status || '-'}</Typography>
            </OverviewFieldBox>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}
